import React, { useState, useMemo, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { useLocation, useNavigate } from 'react-router-dom';
import { menuPermissions, USER_ROLES, isSidebarItemDisabled } from '@/config/permissions';
import { useIsEsn } from '@/hooks/queries/useIsEsn';
import { useDisabledSidebarItems } from '@/hooks/queries/useDisabledSidebarItems';
import { useTeamContext } from '@/hooks/useTeamContext';
import { scheduleSidebarRoutePrefetch } from '@/lib/navigation/prefetchSidebarRoute';
import UserMenu from './UserMenu';
import { SidebarChrome, useSidebarChromeState } from './SidebarChrome';
import {
  Sidebar as ShadcnSidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  useSidebar,
} from '@/components/ui/sidebar';
import { cn } from '@/lib/utils';
import { getSidebarMenuIcon } from '@/lib/navigation/sidebarMenuIcons';

// Blocs de menu : Opérations | Gestion (admin only) | Équipements | Administration
const MENU_GROUPS = [
  {
    labelKey: 'groupOperations',
    // Files: freelancers only (filtered below); team users keep Files under Gestion.
    items: ['Freelancers', 'Clients', 'Missions', 'Activity Report', 'Availability', 'Files'],
  },
  {
    labelKey: 'groupAccounting',
    // Item-level allowedRoles filter visibility (admin: all; member: Files).
    items: ['Accounting', 'Reporting', 'Files', 'Audit'],
  },
  {
    labelKey: 'groupResources',
    items: ['Assets', 'Devices & Tools', 'FAQ'],
  },
  {
    labelKey: 'groupPartners',
    items: ['OurPartners'],
    allowedRoles: [USER_ROLES.FREELANCER],
  },
  {
    labelKey: 'groupAdmin',
    items: ['Super Admin', 'Staffed Free', 'E-invoicing', 'Emails', 'KYC', 'Templates', 'Blog'],
  },
];

const SidebarIcon = ({ iconType, selected, hovered }) => {
  const iconColor = selected || hovered ? '#222222' : '#FFFFFF';

  const IconComponent = getSidebarMenuIcon(iconType);

  return <IconComponent size={16} color={iconColor} />;
};

SidebarIcon.propTypes = {
  iconType: PropTypes.string.isRequired,
  selected: PropTypes.bool.isRequired,
  hovered: PropTypes.bool,
};

const SidebarMenuItemComponent = ({ item, active, onMenuClick, onPrefetch, t, labelKey }) => {
  const [hovered, setHovered] = useState(false);
  const cancelPrefetchRef = useRef(null);

  const handleMouseEnter = () => {
    setHovered(true);
    cancelPrefetchRef.current = onPrefetch(item.paths[0]);
  };

  const handleMouseLeave = () => {
    setHovered(false);
    cancelPrefetchRef.current?.();
    cancelPrefetchRef.current = null;
  };

  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        size="sm"
        isActive={active}
        onClick={() => onMenuClick(item.paths[0])}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className={cn(
          'min-h-8 py-1.5 mb-0.5 rounded-lg gap-2 cursor-pointer px-3! text-white bg-transparent',
          active ? 'bg-[#FFC44D]! text-[#222222]! data-[active=true]:bg-[#FFC44D]! data-[active=true]:text-[#222222]!' : 'hover:bg-white hover:text-[#222222]'
        )}
      >
        <div className="flex items-center justify-center min-w-[18px] min-h-[18px] [&>svg]:size-4">
          <SidebarIcon iconType={item.iconType} selected={active} hovered={hovered && !active} />
        </div>
        <span className="flex-1 text-[14px] leading-normal text-ellipsis overflow-hidden whitespace-nowrap">{t(`sidebar.${labelKey}`)}</span>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
};

SidebarMenuItemComponent.propTypes = {
  item: PropTypes.object.isRequired,
  active: PropTypes.bool.isRequired,
  onMenuClick: PropTypes.func.isRequired,
  onPrefetch: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  labelKey: PropTypes.string.isRequired,
};

const Sidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isEsn = useIsEsn();
  const disabledSidebarItems = useDisabledSidebarItems();
  const { activeTeamId } = useTeamContext();
  const { setOpenMobile, isNavCompact } = useSidebar();
  const { showTeamSwitcher, getLogoTarget, role } = useSidebarChromeState();

  const handlePrefetch = useCallback(
    (path) =>
      scheduleSidebarRoutePrefetch({
        path,
        role,
        activeTeamId,
      }),
    [role, activeTeamId]
  );

  // Filter menu items based on role, visibility, ESN flag, and team-disabled items
  const filteredMenuItems = menuPermissions.filter((item) => {
    if (item.hidden) return false;
    if (isSidebarItemDisabled(item.label, disabledSidebarItems)) return false;
    // Afficher Clients uniquement si ESN confirmé (évite apparition → disparition → réapparition)
    if (item.requireEsn && isEsn !== true) return false;
    if (!item.allowedRoles || item.allowedRoles.length === 0) return true;
    if (!role) return false;
    return item.allowedRoles.includes(role);
  });
  
  // Longest matching path wins (e.g. /super-admin/business only highlights Business, not Super Admin)
  const activeMenuPath = useMemo(() => {
    const allPaths = filteredMenuItems.flatMap((item) => item.paths);
    const matching = allPaths.filter((p) => location.pathname === p || location.pathname.startsWith(`${p}/`));
    return matching.sort((a, b) => b.length - a.length)[0] || null;
  }, [filteredMenuItems, location.pathname]);

  const isActive = (paths) => paths.some((p) => p === activeMenuPath);

  const handleMenuClick = (key) => {
    navigate(key);
    if (isNavCompact) {
      setOpenMobile(false);
    }
  };

  const handleLogoClick = () => {
    navigate(getLogoTarget());
    if (isNavCompact) {
      setOpenMobile(false);
    }
  };

  return (
    <ShadcnSidebar
      variant="sidebar"
      className="w-(--sidebar-width) h-full z-20 bg-[#20766F] flex rounded-tr-xl rounded-br-xl flex-col items-start overflow-hidden **:data-[slot=sidebar-inner]:bg-[#20766F] **:data-[sidebar=sidebar]:bg-[#20766F]"
    >
      <SidebarHeader>
        <SidebarChrome
          onLogoClick={handleLogoClick}
          showTeamSwitcher={showTeamSwitcher}
          logoVariant="white"
        />
      </SidebarHeader>

      <SidebarContent className="px-1">
        {MENU_GROUPS.map((group) => {
          if (group.allowedRoles && (!role || !group.allowedRoles.includes(role))) return null;
          const isFreelancer = role === USER_ROLES.FREELANCER;
          const groupItems = filteredMenuItems.filter((item) => {
            if (!group.items.includes(item.label)) return false;
            // Freelancer: Files under Opérations only. Team: Files under Gestion only.
            if (item.label === 'Files') {
              return isFreelancer
                ? group.labelKey === 'groupOperations'
                : group.labelKey === 'groupAccounting';
            }
            return true;
          });
          if (groupItems.length === 0) return null;
          return (
            <SidebarGroup key={group.labelKey} className="group-data-[collapsible=icon]:hidden">
              <SidebarGroupLabel className="text-white/80 text-[12px] font-medium uppercase tracking-wider px-2 mb-1 mt-2 first:mt-0">
                {t(`sidebar.${group.labelKey}`)}
              </SidebarGroupLabel>
              <SidebarMenu className="gap-0">
                {groupItems.map((item) => {
                  const active = isActive(item.paths);
                  const labelKey =
                    item.label === 'Files' && isFreelancer ? 'FilesShort' : item.label;
                  return (
                    <SidebarMenuItemComponent
                      key={`${group.labelKey}-${item.paths[0]}`}
                      item={item}
                      active={active}
                      onMenuClick={handleMenuClick}
                      onPrefetch={handlePrefetch}
                      t={t}
                      labelKey={labelKey}
                    />
                  );
                })}
              </SidebarMenu>
            </SidebarGroup>
          );
        })}
      </SidebarContent>

      <SidebarFooter className="px-2 pb-3">
        <UserMenu />
      </SidebarFooter>
    </ShadcnSidebar>
  );
};

export default Sidebar;
